import React from 'react';
import GuideLayout from '../common/GuideLayout';
import GuideCta from '../common/GuideCta';
import RelatedGuides from '../common/RelatedGuides';
import TemplateCard from '../common/TemplateCard';
import { ALL_GUIDES } from '../common/guideIndex';
import { ArticleContent, ArticleMeta, Block } from './types';
import { plain, rich } from './RichText';
import { AUTHOR } from '../common/author';

const THEME_EYEBROW: Record<ArticleMeta['theme'], string> = {
  preparer: 'Organisation',
  budget: 'Budget',
  invitations: 'Invitations',
  textes: 'Textes',
  'plan-de-table': 'Plan de table',
  'jour-j': 'Jour J',
};

// Temps de lecture : environ 220 mots par minute
function readingMinutes(content: ArticleContent): number {
  const texts: string[] = [content.lead];
  for (const s of content.sections) {
    for (const b of s.blocks) {
      if (b.type === 'p' || b.type === 'answer' || b.type === 'story' || b.type === 'h3') texts.push(b.text);
      else if (b.type === 'list' || b.type === 'checklist') texts.push(...b.items);
      else if (b.type === 'steps' || b.type === 'cards' || b.type === 'templates')
        b.items.forEach((i) => texts.push(i.title, i.text));
      else if (b.type === 'table') b.rows.forEach((r) => texts.push(...r));
    }
  }
  content.faq.forEach((f) => texts.push(f.q, f.a));
  const words = texts.map(plain).join(' ').split(/\s+/).length;
  return Math.max(2, Math.round(words / 220));
}

const renderBlock = (b: Block, i: number): React.ReactNode => {
  switch (b.type) {
    case 'p':
      return <p key={i}>{rich(b.text)}</p>;
    case 'answer':
      return (
        <p key={i} className="mg-answer">
          {rich(b.text)}
        </p>
      );
    case 'story':
      return (
        <aside key={i} className="mg-story">
          <img src={AUTHOR.avatar} alt="" width={44} height={44} loading="lazy" />
          <div>
            <p className="mg-story-title">Mon conseil</p>
            <p>{rich(b.text)}</p>
          </div>
        </aside>
      );
    case 'h3':
      return (
        <h3 key={i} className="mg-subtitle">
          {b.text}
        </h3>
      );
    case 'list':
      return (
        <ul key={i} className="mg-bullets">
          {b.items.map((item) => (
            <li key={item}>{rich(item)}</li>
          ))}
        </ul>
      );
    case 'checklist':
      return (
        <ul key={i} className="mg-checklist">
          {b.items.map((item) => (
            <li key={item}>
              <span className="mg-box" aria-hidden="true" />
              <span className="mg-task">{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol key={i} className="mg-steps">
          {b.items.map((s) => (
            <li key={s.title}>
              <h3>{s.title}</h3>
              <p>{rich(s.text)}</p>
            </li>
          ))}
        </ol>
      );
    case 'cards':
      return (
        <div key={i} className="mg-grid">
          {b.items.map((c) => (
            <div key={c.title} className="mg-card">
              <h3>{c.title}</h3>
              <p>{rich(c.text)}</p>
            </div>
          ))}
        </div>
      );
    case 'table':
      return (
        <div key={i} className="mg-table-scroll">
          <table className="mg-table mg-compare">
            <thead>
              <tr>
                {b.head.map((h) => (
                  <th key={h} scope="col">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map(([first, ...rest]) => (
                <tr key={first}>
                  <th scope="row">{rich(first)}</th>
                  {rest.map((cell, j) => (
                    <td key={j}>{rich(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'templates':
      return (
        <div key={i} className="mg-template-group">
          {b.title && <h3>{b.title}</h3>}
          {b.intro && <p className="mg-template-intro">{b.intro}</p>}
          <div className="mg-templates">
            {b.items.map((t) => (
              <TemplateCard key={t.title} template={t} />
            ))}
          </div>
        </div>
      );
    case 'cta':
      return <GuideCta key={i} {...b.cta} />;
  }
};

// Page d'un article du blog : même mise en page que les guides (sommaire, FAQ,
// données structurées), puis « À lire aussi » vers la page pilier et le thème.
const ArticlePage: React.FC<{ meta: ArticleMeta; content: ArticleContent }> = ({ meta, content }) => {
  const path = `/${meta.slug}`;
  const relatedPaths = [content.pillar, ...(content.related ?? [])];
  const sameTheme = ALL_GUIDES.filter((g) => g.theme === meta.theme && g.to !== path && !relatedPaths.includes(g.to));
  const related = [
    ...relatedPaths.map((to) => ALL_GUIDES.find((g) => g.to === to)).filter(<T,>(g: T | undefined): g is T => !!g),
    ...sameTheme,
  ].slice(0, 3);

  return (
    <GuideLayout
      eyebrow={content.eyebrow || `Guide · ${THEME_EYEBROW[meta.theme]}`}
      title={meta.title}
      updated={content.updated}
      published={content.published}
      readingMinutes={readingMinutes(content)}
      lead={content.lead}
      topCta={<GuideCta {...content.cta} />}
      toc={[...content.sections.map((s) => ({ id: s.id, label: s.title })), { id: 'faq', label: 'Questions fréquentes' }]}
      faq={content.faq}
    >
      {content.sections.map((s) => (
        <section key={s.id} id={s.id}>
          <h2>{s.title}</h2>
          {s.blocks.map(renderBlock)}
        </section>
      ))}
      <RelatedGuides items={related} />
    </GuideLayout>
  );
};

export default ArticlePage;
